import AuthApiService from "app/Service/Auth/AuthApiService"
import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios'
import {fromUnixTime, isFuture, subSeconds} from "date-fns"
import i18n from "i18next"
import Logger from "logger/logger"
import {toast} from "react-toastify"
import {authQuery} from "store/auth/auth.store"
import {apiGetBaseUrl} from "utils/Api/apiGetBaseUrl"
import {lcFirst} from "utils/stringFunctions"

interface Claims {
  iat: string,
  exp: string,
  id: string,
  username: string,
}

class Api {
  private instance: AxiosInstance | undefined

  private get api(): AxiosInstance {
    return this.instance ? this.instance : this.initAxios()
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get(url, config)
  }

  public post<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config)
  }

  public put<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config)
  }

  public patch<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config)
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config)
  }

  private initAxios() {
    const baseURL = apiGetBaseUrl()

    Logger('API.init', 'url', document.location,)
    const http = axios.create({
      baseURL
    })

    const {refreshToken, logout} = AuthApiService()

    // We intercept all requests to check if we still have a valid token. If the token is not valid anymore,
    // we should check if we have a cookie to refresh the token with. In this case we will make a tokenRefreshRequest,
    // otherwise we should be logged out.
    http.interceptors.request.use(
      async config => {
        const controller = new AbortController()
        config.signal = controller.signal

        const urlStr = config.url?.toString()

        Logger('API.intercept.request.url', urlStr, tokenIsExpired(urlStr))

        if (config.url && config.url.toString().indexOf('auth/') > -1) {
          config.withCredentials = true
          return setBaseHeaders(config)
        }

        if (authQuery.getValue().isUpdatingToken)
          return delayRequest(config).then((resolved) => {
            Logger('API.intercept.request.delay.resolved', resolved)
            return resolved
          }).catch((caught) => {
            Logger('API.intercept.request.delay.catch', caught)
            controller.abort()
            return config
          })

        if (authQuery.getValue().isAuthenticated && tokenIsExpired(urlStr)) {
          Logger('API.intercept.request.tokenIsExpired.refreshToken', urlStr)

          try {
            await refreshToken()
          } catch (e) {
            controller.abort()
            logout()
            return config
          }
        }

        return setRequestHeaders(config)
      }
    )

    http.interceptors.response.use(
      onFulfilled => {
        Logger('API.intercept.response.fulfilled', onFulfilled.config.url, onFulfilled)
        return onFulfilled
      }
      , onRejected => {
        Logger('API.intercept.response.rejected', onRejected)
        APIErrorHandler(onRejected)
        return Promise.reject(onRejected)
      }
    )

    this.instance = http
    return http
  }
}

function delayRequest(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
  Logger('API.intercept.request.delay', config.url)
  // Return a new promise that will resolve when time has passed
  // Gets called recursively until the token has been refreshed
  return new Promise((resolve, reject) => setTimeout(() => {
    Logger('API.intercept.request.delay.startTimeout', config.url)
    // Delay some more if we are still requesting a new token
    if (authQuery.getValue().isUpdatingToken) {
      Logger('API.intercept.request.delay.delayFurther', config.url)
      resolve(delayRequest(config))
      // if we failed to authenticate, don't finish the request
    } else if (!authQuery.getValue().isAuthenticated) {
      Logger('API.intercept.request.delay.reject', config.url)
      reject()
    } else {
      Logger('API.intercept.request.delay.resolve', config.url)
      // We are not updating the token anymore, and we are still authenticated, so continue the request
      resolve(setRequestHeaders(config))
    }
  }, 50, config))
}

function tokenIsExpired(urlStr?: string) {
  const token = authQuery.getValue().token

  if (!token) return false  // It can not be expired if you don't have a token!

  // Get the claims
  const claims = getTokenClaims(token)

  // Convert to date
  const expDate = fromUnixTime(parseInt(claims['exp']))
  const expDateAdded = subSeconds(expDate, 5)

  Logger('API.token.verify', !isFuture(expDateAdded), expDate.getMinutes() + ':' + expDate.getSeconds(), expDateAdded.getMinutes() + ':' + expDateAdded.getSeconds(), urlStr)

  // Return true if the date is still in the future
  return !isFuture(expDateAdded)
}

const getTokenClaims: (token: string) => Claims = (token) => {
  // Split the JWT in 3 parts
  const tokenArr = token.split('.')

  if (tokenArr.length !== 3) {
    throw new Error("Incorrect number of token parts")
  }

  // Base64 decode the second part, containing the claims
  const claimsString = window.atob(tokenArr[1])

  // Return parsed claims
  return JSON.parse(claimsString)
}

const APIErrorHandler = (error: AxiosError) => {
  Logger('API.error', error)
  // The request was made and the server responded with a status code
  // that falls out of the range of 2xx
  if (error.response) {
    const data = error.response.data

    Logger('API.error.data', data)

    let nameSpace = data.namespace

    if (data.message && data.namespace) {
      const id = `${nameSpace}-${data.message}`
      const toastMessage = i18n.t(`api.${data.message}`, {ns: nameSpace})
      if (data.message === "cookie.missing")
        toast.info(toastMessage, {toastId: id})
      else
        toast.error(toastMessage, {toastId: id})
    } else {
      if (!data.hasOwnProperty('errors'))
        toast.error(`Unknown API response`)
      else {
        const errors = data.errors
        Object.keys(errors).map(dtoKey => {
          Logger('API.error.dto.dtoKey', dtoKey)
          Object.keys(errors[dtoKey]).map(field => {
            Logger('API.error.dto.field', field)
            const message = errors[dtoKey][field].message
            Logger('API.error.dto.message', message)

            let translationKey = `api.${lcFirst(dtoKey)}.${field}.${message}`

            if (field === 'entityId') {
              translationKey = `api.${field}.${message}`
              nameSpace = 'Module.Entity'
            } else {
              // We need to set it again, so if there's another field after entityId, it does not keep entityId namespace
              nameSpace = data.namespace
            }

            toast.error(i18n.t(translationKey, {ns: nameSpace}), {
              toastId: `${dtoKey.toLowerCase()}-${field.toLowerCase()}`
            })
          })
        })
      }
    }

    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
  } else if (error.request) {
    Logger('API.error.request', error)
    toast.error(`API is unresponsive`)

    // This is not an AxiosError but most probably a JS error
    // Cancelled requests also end up here
  } else if (error.message) {
    Logger('API.Error.message', error)
    if (error.message !== "canceled") {
      toast.error(error.message, {
        toastId: error.message
      })
    }

    // Something happened in setting up the request that triggered an Error
  } else {
    toast.error(`We dont know what went wrong`)
    Logger('API.Error.message', error.message)
  }
}

function setRequestHeaders(config: AxiosRequestConfig) {
  config = setBaseHeaders(config)
  // Set headers including new token
  config.headers = Object.assign({}, config.headers, {
    'Authorization': `Bearer ${authQuery.getValue().token}`,
  })

  return config
}

function setBaseHeaders(config: AxiosRequestConfig) {
  config.headers = Object.assign({}, config.headers, {
    'Accept': 'application/json',
  })

  return config
}

const API = new Api()


export default API
